import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLanguage } from "@/components/language-provider";
import { AppLayout } from "@/components/app-layout";
import { FileText, Send, Clock, CheckCircle, Plus, FileUp, Loader2, AlertTriangle, Building2 } from "lucide-react";
import { useEffect, useState } from "react";
import { API_BASE_URL } from "@/lib/config";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function safeParseJSON(str: string | null): any {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function extractData(response: any): any {
  // Handle wrapped response { success: true, data: {...} }
  if (response && typeof response === 'object' && 'data' in response) {
    return response.data;
  }
  return response;
}

import { OnboardingDialog } from "@/components/onboarding-dialog";
import { StatusBadge } from "@/components/status-badge";
import { LIVE_QUERY_OPTIONS, pollWhileListPending } from "@/lib/live-documents";
import { planLabel } from "@/lib/plan";
import { DocumentLimitBanner, NewDocumentButton } from "@/components/document-limit";
import { useDocumentLimit } from "@/lib/document-limit";
import { crmBadge } from "@/lib/crm-source";

/**
 * A value that is still loading: a soft shimmer the size of the number, never a "0" that jumps to the real value.
 * When it arrives it fades in; `delay` (ms) staggers the reveal so the page fills from top to bottom.
 */
function Fill({ ready, delay = 0, w = "w-12", h = "h-7", children }: { ready: boolean; delay?: number; w?: string; h?: string; children: React.ReactNode }) {
  if (!ready) return <span className={`fs-shimmer ${w} ${h}`} aria-hidden="true" />;
  return <span className="fs-fill inline-block" style={{ animationDelay: `${delay}ms` }}>{children}</span>;
}

export default function Dashboard() {
  const [, setLocation] = useLocation();
  // Read the session synchronously so the first frame is already the dashboard (no spinner frame before it).
  const [user, setUser] = useState<any>(() => safeParseJSON(localStorage.getItem("user")));
  const [isInitialized, setIsInitialized] = useState(() => !!localStorage.getItem("token") && !safeParseJSON(localStorage.getItem("user"))?.mustChangePassword);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ currentCount: number; limit: number; plan: string } | null>(null);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const { t, language } = useLanguage();
  const limitQuery = useDocumentLimit();
  const documentLimit = limitQuery.data;

  // Function to check document limit before navigating to upload
  const checkLimitAndNavigate = async () => {
    setCheckingLimit(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/documents/check-limit`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.canUpload) {
          // User can upload: straight to the wizard
          setLocation("/documents/new");
        } else {
          // User has reached limit, show dialog
          setLimitInfo({
            currentCount: data.currentCount,
            limit: data.limit,
            plan: data.plan
          });
          setLimitDialogOpen(true);
        }
      } else {
        // If check fails, allow navigation (fail open; the wizard checks again)
        setLocation("/documents/new");
      }
    } catch (error) {
      // If check fails, allow navigation (fail open; the wizard checks again)
      setLocation("/documents/new");
    } finally {
      setCheckingLimit(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    
    if (!token) {
      setLocation("/login");
      return;
    }
    
    const parsed = safeParseJSON(storedUser);
    if (parsed) {
      // Force password change for invited users
      if (parsed.mustChangePassword) {
        setLocation("/profile");
        return;
      }
      setUser(parsed);
    }
    setIsInitialized(true);
  }, [setLocation]);

  // Same keys as the rest of the app ("/api/..."), so a trash move or a cancel elsewhere refreshes here too.
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["/api/stats"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/api/stats`, { headers: getAuthHeaders() });
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setLocation("/login");
          throw new Error("Unauthorized");
        }
        throw new Error("Failed to fetch stats");
      }
      const json = await res.json();
      return extractData(json);
    },
    enabled: isInitialized,
    retry: false,
    ...LIVE_QUERY_OPTIONS,
  });

  const { data: recentDocs, isLoading: docsLoading } = useQuery<any>({
    queryKey: ["/api/documents"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/api/documents`, { headers: getAuthHeaders() });
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setLocation("/login");
          throw new Error("Unauthorized");
        }
        throw new Error("Failed to fetch documents");
      }
      const json = await res.json();
      return extractData(json);
    },
    enabled: isInitialized,
    retry: false,
    ...LIVE_QUERY_OPTIONS,
    refetchInterval: pollWhileListPending,
  });

  // Top to bottom: the cards fill once their numbers are in; the list below waits for the cards, so the page never
  // fills from the bottom up when the documents happen to answer first.
  const cardsReady = !statsLoading && !limitQuery.loading;
  const fmt = (n: unknown) => Number(n || 0).toLocaleString(language === "pt" ? "pt-BR" : "en-US");
  const listReady = cardsReady && !docsLoading;

  // Redirecting (no session / must change password): nothing to show for that instant.
  if (!isInitialized) return null;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLocation("/");
  };
  
  if (!user) return null;

  // Whether this account has already used FillSign (undefined until the stats or the documents arrive).
  const hasDocuments: boolean | undefined = stats
    ? Number((stats as any).documentsCount ?? 0) > 0 || Number((stats as any).signaturesUsed ?? 0) > 0
    : Array.isArray(recentDocs) ? recentDocs.length > 0 : undefined;

  // Average time from creation to the last signature, over completed documents already loaded.
  const avgSigning = (() => {
    const docs: any[] = Array.isArray(recentDocs) ? recentDocs : [];
    const spans = docs
      .filter((d) => d.status === "completed" && d.createdAt)
      .map((d) => {
        const ends = (d.signers || []).map((x: any) => x.signedAt).filter(Boolean).map((x: string) => new Date(x).getTime());
        const end = ends.length ? Math.max(...ends) : (d.completedAt ? new Date(d.completedAt).getTime() : NaN);
        return end - new Date(d.createdAt).getTime();
      })
      .filter((ms) => Number.isFinite(ms) && ms > 0);
    if (spans.length === 0) return { label: "—", n: 0 };
    const avgMs = spans.reduce((a, b) => a + b, 0) / spans.length;
    const h = avgMs / 36e5;
    const label = h < 1 ? `${Math.max(1, Math.round(avgMs / 6e4))} min` : h < 48 ? `${Math.round(h)} h` : `${(h / 24).toFixed(1).replace(/\.0$/, "")} ${language === "pt" ? "dias" : "days"}`;
    return { label, n: spans.length };
  })();

  return (
    <AppLayout user={user} onLogout={handleLogout} plan={stats?.plan}>
      <OnboardingDialog userId={user?.id} plan={stats?.plan ?? user?.plan} hasDocuments={hasDocuments} />
        <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">{t("dashboard.welcome")}, {user.firstName || "there"}</h1>
            <p className="text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
          </div>
          <NewDocumentButton
            onClick={checkLimitAndNavigate}
            disabled={checkingLimit}
            icon={checkingLimit ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            testId="button-new-document"
            data-guide="new-document"
          >
            {t("dashboard.newDocument")}
          </NewDocumentButton>
        </div>

        <DocumentLimitBanner className="mb-6" />

        <div className="grid gap-3 sm:gap-6 grid-cols-2 lg:grid-cols-5 mb-6 sm:mb-8">
          <Link href="/documents">
            <Card className="cursor-pointer hover-elevate transition-all" data-testid="card-documents">
              <CardHeader className="flex flex-row items-center justify-between gap-1 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{t("dashboard.documents")}</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-xl sm:text-2xl font-bold"><Fill ready={cardsReady} delay={0}>{stats ? fmt(stats.documentsCount) : "—"}</Fill></div>
              </CardContent>
            </Card>
          </Link>
          <Link href="/signatures?status=pending">
            <Card className="cursor-pointer hover-elevate transition-all" data-testid="card-pending">
              <CardHeader className="flex flex-row items-center justify-between gap-1 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{t("dashboard.pending")}</CardTitle>
                <Clock className="h-4 w-4 text-yellow-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-xl sm:text-2xl font-bold"><Fill ready={cardsReady} delay={70}>{stats ? fmt(stats.signaturesPending) : "—"}</Fill></div>
              </CardContent>
            </Card>
          </Link>
          <Link href="/signatures?status=signed">
            <Card className="cursor-pointer hover-elevate transition-all" data-testid="card-completed">
              <CardHeader className="flex flex-row items-center justify-between gap-1 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{t("dashboard.completed")}</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-xl sm:text-2xl font-bold"><Fill ready={cardsReady} delay={140}>{stats ? fmt(stats.signaturesCompleted) : "—"}</Fill></div>
              </CardContent>
            </Card>
          </Link>
          <Card data-testid="card-avg-signing-time">
            <CardHeader className="flex flex-row items-center justify-between gap-1 p-3 sm:p-6 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground leading-tight line-clamp-2 sm:truncate">{t("dashboard.avgSigningTime")}</CardTitle>
              <Clock className="h-4 w-4 text-blue-500 flex-shrink-0" />
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="text-xl sm:text-2xl font-bold"><Fill ready={listReady} delay={210} w="w-16">{avgSigning.label}</Fill></div>
              <p className="text-[11px] text-muted-foreground"><Fill ready={listReady} delay={240} w="w-24" h="h-3">{avgSigning.n > 0 ? `${avgSigning.n} ${t("dashboard.avgSigningBasis")}` : t("dashboard.avgSigningNone")}</Fill></p>
            </CardContent>
          </Card>
          <Card data-testid="card-usage">
            <CardHeader className="flex flex-row items-center justify-between gap-1 p-3 sm:p-6 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{t("dashboard.usage")}</CardTitle>
              <Send className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              {!cardsReady ? (
                <>
                  <div className="text-xl sm:text-2xl font-bold"><Fill ready={false} w="w-16">{null}</Fill></div>
                  <p className="text-[11px] text-muted-foreground"><Fill ready={false} w="w-24" h="h-3">{null}</Fill></p>
                </>
              ) : documentLimit && documentLimit.limit > 0 ? (
                // Same counter that locks the "new document" buttons (documents sent this month), not signatures received.
                <>
                  <div className="text-xl sm:text-2xl font-bold" data-testid="usage-documents">
                    <Fill ready delay={280}>{documentLimit.currentCount}<span className="text-xs sm:text-sm text-muted-foreground font-normal">/{documentLimit.limit}</span></Fill>
                  </div>
                  <p className="text-[11px] text-muted-foreground"><Fill ready delay={300}>{language === "pt" ? "documentos este mês" : "documents this month"}</Fill></p>
                </>
              ) : (
                <div className="text-xl sm:text-2xl font-bold">
                  <Fill ready delay={280}>
                    {stats ? stats.signaturesUsed || 0 : "—"}
                    {stats?.plan === "free" && <span className="text-xs sm:text-sm text-muted-foreground font-normal">/{stats?.signatureLimit || 3}</span>}
                  </Fill>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle>{t("dashboard.recentDocuments")}</CardTitle>
              <Link href="/documents">
                <Button variant="ghost" size="sm">{t("dashboard.viewAll")}</Button>
              </Link>
            </CardHeader>
            <CardContent>
              {!listReady ? (
                <div className="space-y-2" aria-busy="true" data-testid="recent-loading">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center justify-between gap-2 sm:gap-4 p-2 sm:p-3 rounded-md border bg-muted/30">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <span className="fs-shimmer h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0" />
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <span className="fs-shimmer block h-3.5" style={{ width: `${[62, 48, 70, 55, 40][i]}%` }} />
                          <span className="fs-shimmer block h-3 w-20" />
                        </div>
                      </div>
                      <span className="fs-shimmer h-5 w-16 rounded-full flex-shrink-0" />
                    </div>
                  ))}
                </div>
              ) : !recentDocs?.length ? (
                <div className="fs-fill text-center py-8 text-muted-foreground">
                  <FileUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("ui.no_documents_yet")}</p>
                  <Link href="/documents">
                    <Button variant="ghost">{t("ui.upload_your_first_document")}</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentDocs.slice(0, 5).map((doc: any, i: number) => (
                    <Link key={doc.id} href={`/documents/${doc.id}`}>
                      <div 
                        className="fs-fill flex items-center justify-between gap-2 sm:gap-4 p-2 sm:p-3 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                        style={{ animationDelay: `${120 + i * 60}ms` }}
                        data-testid={`card-document-${doc.id}`}
                      >
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                          <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate flex items-center gap-1">
                              {doc.name}
                              {doc.crmSource && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 ml-1 bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800 shrink-0">
                                  <Building2 className="h-2.5 w-2.5 mr-0.5 inline" />{crmBadge(doc.crmSource)}
                                </Badge>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">{new Date(doc.createdAt).toLocaleDateString(language === "pt" ? "pt-BR" : "en-US")}</p>
                          </div>
                        </div>
                        <StatusBadge status={doc.status} className="flex-shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("dashboard.quickActions")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <NewDocumentButton
                variant="outline"
                className="w-full justify-start"
                onClick={checkLimitAndNavigate}
                disabled={checkingLimit}
                icon={checkingLimit ? <Loader2 className="mr-3 h-4 w-4 animate-spin" /> : <FileUp className="mr-3 h-4 w-4" />}
                testId="action-upload"
              >
                {t("dashboard.uploadDocument")}
              </NewDocumentButton>
              <Link href="/signatures" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="action-send">
                  <Send className="mr-3 h-4 w-4" /> {t("dashboard.viewSignatures")}
                </Button>
              </Link>
              {(stats?.plan === "pro" || stats?.plan === "enterprise") && (
                <Link href="/settings?tab=email-templates" className="block fs-fill">
                  <Button variant="outline" className="w-full justify-start" data-testid="action-template">
                    <FileText className="mr-3 h-4 w-4" /> {t("ui.email_templates")}
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Document Limit Reached Dialog */}
        <Dialog open={limitDialogOpen} onOpenChange={setLimitDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                {t("errors.documentLimitReached")}
              </DialogTitle>
              <DialogDescription>
                {t("errors.documentLimitReachedDesc")}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.usage")}</p>
                  <p className="text-2xl font-bold">
                    {limitInfo?.currentCount || 0} / {limitInfo?.limit || 0}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {t("common.plan")}: {planLabel(limitInfo?.plan, t)}
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setLimitDialogOpen(false)}>
                {t("common.close")}
              </Button>
              <Link href="/settings?tab=subscription">
                <Button onClick={() => setLimitDialogOpen(false)}>
                  {t("dashboard.upgradeNow")}
                </Button>
              </Link>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </AppLayout>
  );
}
